import React, { useState } from 'react';
import validations from '../../../lib/helpers/validations';
import { gql } from '@apollo/client';
import { useRequestVerificationEmail_CreateVerificationTokenMutation } from '#types';
import rpcShared from '@rockpapercoin/rpc-shared';
import { useToast } from '../../../hooks/useToast';
import { FormControl, FormHelperText, InputLabel, Input } from '@mui/material';
import styles from './index.module.scss';
const {
	strings: { errorMessages },
} = rpcShared;
import { Button } from '../../../blocks/Button';

export type RequestVerificationEmailProps = {
	onSuccess: () => void;
	email?: string;
	promoCode?: string;
	submitLabel?: string;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const requestVerificationEmail_createVerificationToken = gql`
	mutation requestVerificationEmail_createVerificationToken(
		$data: CreateVerificationTokenInput!
	) {
		createVerificationToken(data: $data)
	}
`;

export const RequestVerificationEmail: React.FC<
	RequestVerificationEmailProps
> = ( props ) => {
	const { showError } = useToast();
	const [ validation, setValidation ] = useState<string | undefined>( undefined );
	const [ email, setEmail ] = useState<string>( props.email || '' );
	const [ createVerificationToken, { loading } ] =
		useRequestVerificationEmail_CreateVerificationTokenMutation();
	const onValidate = () => {
		const emailValidation = validations.validateEmail( email );
		if ( emailValidation ) {
			setValidation( emailValidation );
			return false;
		} else {
			setValidation( undefined );
			return true;
		}
	};

	const handleSubmit = async ( e: React.FormEvent<HTMLFormElement> ) => {
		e.preventDefault();
		if ( !onValidate() ) {
			return;
		}
		const res = await createVerificationToken( {
			variables: {
				data: {
					email,
					...( props.promoCode ? { promoCode: props.promoCode } : {} ),
				},
			},
		} );
		if ( !res.errors ) {
			setValidation( undefined );
			setEmail( '' );
			props.onSuccess();
		} else if ( res.errors ) {
			const isNotAvailableError = res.errors.find( ( e ) =>
				e.message.includes( errorMessages.emailInUseByAnotherUser )
			);
			const isInsufficientlyUniqueError = res.errors.find( ( e ) =>
				e.message.includes( errorMessages.tooManySimilarEmails )
			);
			if ( isInsufficientlyUniqueError ) {
				setValidation( 'Please use a different email address.' );
			} else if ( isNotAvailableError ) {
				setValidation(
					'A user with this email address already exists, did you mean to log in?'
				);
			} else {
				res.errors.map( ( error ) => showError( error.message ) );
			}
		}
	};
	return (
		<form className={ styles.form } onSubmit={ handleSubmit } noValidate>
			<FormControl variant='standard' className={ styles.input }>
				<InputLabel htmlFor='email' shrink>
					Email
				</InputLabel>
				<Input
					id='email'
					data-cy='email-field'
					placeholder='you@company.com'
					slotProps={ { input: { type: 'email', inputMode: 'email' } } }
					value={ email }
					onChange={ ( e ) => setEmail( e.target.value ) }
					error={ !!validation }
					onBlur={ () => onValidate() }
				/>
				{ validation ? (
					<FormHelperText className={ styles.error }>{ validation }</FormHelperText>
				) : null }
			</FormControl>
			<Button
				className={ styles.submitButton }
				data-cy='sign-up-button'
				type='submit'
				color='primary'
				variant='contained'
				loading={ loading }
			>
				{ props.submitLabel || 'Sign up' }
			</Button>
		</form>
	);
};
